import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUsers,
  faWallet,
  faCheckCircle,
  faClock,
  faFileInvoiceDollar,
  faChartLine,
  faUserPlus,
  faMoneyBillWave,
  faUndo,
  faClipboardList,
  faIdCard,
  faBookOpen,
  faArrowRight,
} from "@fortawesome/free-solid-svg-icons";
import { Row, Col, Button, Table, Badge } from "@themesberg/react-bootstrap";

import { adminDashboardData } from "../../../pages/api";
import { Routes } from "../../../routes";
import DashboardStatCard from "../../../components/dashboard/DashboardStatCard";
import DashboardSection from "../../../components/dashboard/DashboardSection";
import EmptyState from "../../../components/dashboard/EmptyState";
import LoadingSkeleton from "../../../components/dashboard/LoadingSkeleton";
import {
  formatCurrency,
  formatNumber,
  capitalize,
  getPaymentStatusBadge,
  getRegistrationBadge,
  getActivityBadge,
} from "../../../components/dashboard/dashboardHelpers";

const getNumberValue = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const mapRecentTransaction = (item) => {
  const typeValue = String(item?.type || "").trim().toLowerCase();
  const amount = Number(item?.amount || 0);

  return {
    id: item?.id ?? item?.transaction_id ?? Date.now(),
    type: capitalize(item?.type || "—"),
    amount: typeValue === "debit" ? -Math.abs(amount) : Math.abs(amount),
    paymentMethod: capitalize(item?.payment_method || "—"),
    status: capitalize(item?.status || "—"),
    rawType: typeValue,
  };
};

const REPORT_SHORTCUTS = [
  {
    title: "Payment Report",
    description: "Successful, pending, and failed payments",
    icon: faMoneyBillWave,
    to: Routes?.AdminReports?.path || "/admin-reports",
    hint: "Select Payment report type",
  },
  {
    title: "Course Report",
    description: "Course enrollments.",
    icon: faBookOpen,
    to: Routes?.AdminReports?.path || "/admin-reports",
    hint: "Select Payment report type",
  },
  {
    title: "Opt-out Report",
    description: "Course opt-out status and finalization",
    icon: faUndo,
    to: Routes?.AdminReports?.path || "/admin-reports",
    hint: "Select Opt Out report type",
  },
  {
    title: "Commission Report",
    description: "Commission generated and processed",
    icon: faChartLine,
    to: Routes?.AdminReports?.path || "/admin-reports",
    hint: "Select Commission report type",
  },
  {
    title: "Wallet Report",
    description: "User wallet balances and movements",
    icon: faWallet,
    to: Routes?.AdminReports?.path || "/admin-reports",
    hint: "Select Wallet report type",
  },
  {
    title: "User Report",
    description: "Registration and user activity",
    icon: faUsers,
    to: Routes?.AdminReports?.path || "/admin-reports",
    hint: "Select Registration report type",
  },
  {
    title: "Transaction History",
    description: "All platform transactions",
    icon: faClipboardList,
    to: Routes?.AdminTransactionHistory?.path || "/admin-transaction-history",
  },
  {
    title: "User Management",
    description: "Manage users and accounts",
    icon: faIdCard,
    to: Routes?.AdminUserManagement?.path || "/admin-user-management",
  },
];


const CARD_SCROLL_STYLE = {
  overflow: "auto",
  maxHeight: "17.2rem",
};



export default function AdminDashboard() {
  const [registrations, setRegistrations] = useState(0);
  const [activeUsers, setActiveUsers] = useState(0);
  const [inactiveUsers, setInactiveUsers] = useState(0);
  const [collections, setCollections] = useState(0);
  const [recentRegisters, setRecentRegisters] = useState([]);
  const [recentTxns, setRecentTxns] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAdminDashboard = async () => {
      try {
        setLoading(true);
        const response = await adminDashboardData();
        const data = response?.data?.data || response?.data || {};

        setRegistrations(getNumberValue(data?.registrations));
        setActiveUsers(getNumberValue(data?.active_users));
        setInactiveUsers(getNumberValue(data?.inactive_users));
        setCollections(getNumberValue(data?.collections));
        setRecentRegisters(Array.isArray(data?.recent_registers) ? data.recent_registers : []);
        setRecentTxns(
          Array.isArray(data?.recent_transactions)
            ? data.recent_transactions.map(mapRecentTransaction)
            : []
        );
        setActivityLogs(Array.isArray(data?.activity_logs) ? data.activity_logs : []);
      } catch (error) {
        console.error("Failed to load admin dashboard data:", error);
        setRecentRegisters([]);
        setRecentTxns([]);
        setActivityLogs([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAdminDashboard();
  }, []);

  return (
    <div className="dashboard-page">
      <div className="d-flex justify-content-between flex-wrap align-items-center gap-3 mb-4 py-4">
        <div>
          <h2 className="h4 mb-1" style={{ color: "#111827" }}>Admin Dashboard</h2>
          <p className="text-muted mb-0 small">
            Business overview, recent activity, and quick access to reports.
          </p>
        </div>
        <Button as={Link} to={Routes?.AdminReports?.path || "/admin-reports"} variant="outline-primary">
          <FontAwesomeIcon icon={faClipboardList} className="me-2" />
          Open Reports
        </Button>
      </div>

      {loading ? (
        <LoadingSkeleton count={4} />
      ) : (
        <>
          <Row className="g-4 mb-4">
            <Col xs={12} sm={6} xl={3}>
              <DashboardStatCard
                title="Total Users"
                subtitle="All registered users"
                value={formatNumber(registrations)}
                icon={faUsers}
                tone="primary"
              />
            </Col>
            <Col xs={12} sm={6} xl={3}>
              <DashboardStatCard
                title="Active Users"
                subtitle="Users with active access"
                value={formatNumber(activeUsers)}
                icon={faCheckCircle}
                tone="success"
              />
            </Col>
            <Col xs={12} sm={6} xl={3}>
              <DashboardStatCard
                title="Inactive Users"
                subtitle="Currently inactive"
                value={formatNumber(inactiveUsers)}
                icon={faClock}
                tone="warning"
              />
            </Col>
            <Col xs={12} sm={6} xl={3}>
              <DashboardStatCard
                title="Total Collections"
                subtitle="Payments received"
                value={formatCurrency(collections, { decimals: 2 })}
                icon={faWallet}
                tone="primary"
              />
            </Col>
          </Row>

          {/* <Row className="g-4 mb-4">
            <Col xs={12} sm={6} xl={3}>
              <DashboardStatCard
                title="Commission Overview"
                subtitle="View in Commission Report"
                value="Reports"
                icon={faChartLine}
                tone="info"
                footer={
                  <Link to={Routes?.AdminReports?.path || "/admin-reports"} className="small">
                    Open commission report →
                  </Link>
                }
              />
            </Col>
            <Col xs={12} sm={6} xl={3}>
              <DashboardStatCard
                title="Opt-out Overview"
                subtitle="View in Opt-out Report"
                value="Reports"
                icon={faUndo}
                tone="danger"
                footer={
                  <Link to={Routes?.AdminReports?.path || "/admin-reports"} className="small">
                    Open opt-out report →
                  </Link>
                }
              />
            </Col>
            <Col xs={12} sm={6} xl={3}>
              <DashboardStatCard
                title="Withdrawals"
                subtitle="Pending withdrawal requests"
                value="Manage"
                icon={faMoneyBillWave}
                tone="warning"
                footer={
                  <Link
                    to={Routes?.AdminWithdrawalRequests?.path || "/admin-withdrawal-requests"}
                    className="small"
                  >
                    View withdrawal requests →
                  </Link>
                }
              />
            </Col>
            <Col xs={12} sm={6} xl={3}>
              <DashboardStatCard
                title="Courses"
                subtitle="Manage active courses"
                value="Manage"
                icon={faBookOpen}
                tone="dark"
                footer={
                  <Link to={Routes?.AdminCourseList?.path || "/admin-course-list"} className="small">
                    Open course list →
                  </Link>
                }
              />
            </Col>
          </Row> */}

          <DashboardSection
            className="mb-4"
            title="Report Shortcuts"
            subtitle="Quick navigation to admin reports and tools"
          >
            <Row className="g-3">
              {REPORT_SHORTCUTS.map((item) => (
                <Col xs={12} sm={6} lg={3} key={item.title}>
                  <Link to={item.to} className="dashboard-shortcut-card">
                    <div className="dashboard-shortcut-icon">
                      <FontAwesomeIcon icon={item.icon} />
                    </div>
                    <div className="dashboard-shortcut-title">{item.title}</div>
                    <p className="dashboard-shortcut-desc">{item.description}</p>
                    <span className="small text-primary">
                      Open <FontAwesomeIcon icon={faArrowRight} className="ms-1" />
                    </span>
                  </Link>
                </Col>
              ))}
            </Row>
          </DashboardSection>

          <Row className="g-4 mb-4">
            <Col xs={12} xl={7}>
              <DashboardSection
                title="Recent Transactions"
                subtitle="Latest platform payment activity"
                action={
                  <Button
                    as={Link}
                    to={Routes?.AdminTransactionHistory?.path || "/admin-transaction-history"}
                    variant="link"
                    size="sm"
                    className="p-0"
                  >
                    View all
                  </Button>
                }
              >
                <div style={CARD_SCROLL_STYLE}>
                {recentTxns.length === 0 ? (
                  <EmptyState
                    icon={faMoneyBillWave}
                    title="No recent transactions"
                    message="Payment activity will appear here."
                  />
                ) : (
                  <div className="table-responsive">
                    <Table hover className="dashboard-table align-middle mb-0">
                      <thead>
                        <tr>
                          <th>Type</th>
                          <th className="text-center">Amount</th>
                          <th className="text-center">Method</th>
                          <th className="text-center">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentTxns.map((item) => (
                          <tr key={item.id}>
                            <td>{item.type}</td>
                            <td
                              className={`text-center fw-bold ${
                                item.rawType === "credit" ? "text-success" : "text-danger"
                              }`}
                            >
                              {item.rawType === "credit" ? "+" : "−"}
                              {formatCurrency(Math.abs(item.amount))}
                            </td>
                            <td className="text-center">{item.paymentMethod}</td>
                            <td className="text-center">
                              <Badge
                                bg={getPaymentStatusBadge(item.status)}
                                className="px-2 py-1 rounded-pill"
                              >
                                {item.status}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
                </div>
              </DashboardSection>
            </Col>

            <Col xs={12} xl={5}>
              <DashboardSection
                title="Recent Activity"
                subtitle="Latest platform notifications"
              >
                <div style={CARD_SCROLL_STYLE}>
                {activityLogs.length === 0 ? (
                  <EmptyState
                    title="No recent activity"
                    message="System activity will appear here."
                  />
                ) : (
                  activityLogs.map((item) => (
                    <div key={item.id} className="dashboard-activity-item">
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <Badge bg={getActivityBadge(item.type)} className="rounded-pill px-2 py-1">
                          {capitalize(item.type || "Update")}
                        </Badge>
                        <span className="dashboard-activity-time">{item.time}</span>
                      </div>
                      <div className="dashboard-activity-title">{item.title}</div>
                      {item.message && (
                        <div className="dashboard-activity-message">{item.message}</div>
                      )}
                    </div>
                  ))
                )}
                </div>
              </DashboardSection>
            </Col>
          </Row>

          <DashboardSection
            title="New User Registrations"
            subtitle="Most recently registered users"
            action={
              <Button
                as={Link}
                to={Routes?.AdminUserManagement?.path || "/admin-user-management"}
                variant="link"
                size="sm"
                className="p-0"
              >
                View all users
              </Button>
            }
          >
            {recentRegisters.length === 0 ? (
              <EmptyState
                icon={faUserPlus}
                title="No recent registrations"
                message="New user sign-ups will appear here."
              />
            ) : (
              <div className="table-responsive">
                <Table hover className="dashboard-table align-middle mb-0">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Name</th>
                      <th>Email/Username</th>
                      <th>Mobile</th>
                      <th className="text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentRegisters.map((item, index) => (
                      <tr key={item.id}>
                        <td className="fw-semibold">{index + 1}</td>
                        <td className="fw-semibold">{item.name || "—"}</td>
                        <td className="text-muted">{item.email || "—"}</td>
                        <td>{item.mobile_number || "—"}</td>
                        <td className="text-center">
                          <Badge
                            bg={getRegistrationBadge(item.status)}
                            className="px-2 py-1 rounded-pill text-capitalize"
                          >
                            {capitalize(item.status)}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </DashboardSection>
        </>
      )}
    </div>
  );
}
